import { GroupPrices } from "../../GroupPrices";
export class RegisterCartPriceCalculator {
    /**
     * Return an unique ID that is the same for all groups that should have equal family pricing (2nd, 3rd discount)
     */
    static getFamilyDiscountIdFor(group, all) {
        const parents = group.getParentCategories(all, false);
        for (const parent of parents) {
            if (parent.settings.maximumRegistrations === 1) {
                return "category-" + parent.id;
            }
        }
        // Only registrations in same group are elegiable for family discount
        return "group-" + group.id;
    }
    /**
     * Return an unique ID that is the same for all groups that should have equal family pricing (2nd, 3rd discount)
     */
    static getParentCategory(group, all) {
        var _a;
        const parents = group.getParentCategories(all, false);
        return (_a = parents[0]) !== null && _a !== void 0 ? _a : null;
    }
    static calculateSinglePrice(member, registration, members, groups, categories) {
        var _a, _b;
        const item = {
            memberId: member.id,
            groupId: registration.groupId,
            reduced: (_b = (_a = member.details.requiresFinancialSupport) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : false,
            waitingList: registration.waitingList,
            calculatedPrice: 0
        };
        this.calculatePrices([item], [...members, member], groups, categories);
        return item.calculatedPrice;
    }
    static calculateSinglePriceForNewMember(details, registration, members, groups, categories) {
        var _a, _b;
        const member = {
            id: 'calculating-member',
            details,
            registrations: [registration],
        };
        const item = {
            memberId: member.id,
            groupId: registration.groupId,
            reduced: (_b = (_a = details.requiresFinancialSupport) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : false,
            waitingList: registration.waitingList,
            calculatedPrice: 0
        };
        this.calculatePrices([item], [...members, member], groups, categories);
        return item.calculatedPrice;
    }
    /**
     * @param items Items you want to calculate the prices for (should be all the items)
     * @param members All current members, including members that are not present in the items (because we need to know this for family discounts)
     * @param groups All the groups of this organization
     * @param categories All the categories of this organization
     */
    static calculatePrices(items, members, groups, categories, forceDate) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v;
        // Step 1 + 2: 
        // 1. add the group prices
        // 2. group items by items that have a grouped family discount
        //      - First: no discounts or only discount for same group (ordering doesn't matter)
        //      - Second: Discounts for same member in category
        //      - Last: Diuscounts for family members in category
        // Excluding waiting lists
        const groupedItems = [[], new Map(), new Map()];
        const now = forceDate !== null && forceDate !== void 0 ? forceDate : new Date();
        for (const item of items) {
            item.calculatedPrice = 0;
            const group = groups.find(g => g.id === item.groupId);
            const member = members.find(m => m.id === item.memberId);
            if (!group || !member) {
                throw new Error("Invalid item: member or group doesn't exist anymore");
            }
            if (!item.waitingList) {
                const groupPrices = (_a = group.settings.getGroupPrices(now)) !== null && _a !== void 0 ? _a : GroupPrices.create({});
                const category = this.getParentCategory(group, categories);
                if (!category) {
                    throw new Error("Invalid item: category doesn't exist anymore");
                }
                const _item = Object.assign(item, { _groupPrices: groupPrices, _group: group, _category: category });
                // In which priority queue?
                if (groupPrices.onlySameGroup || groupPrices.prices.length <= 1) {
                    // First queue: ordering will never matter
                    groupedItems[0].push(_item);
                }
                else {
                    const prior = groupPrices.sameMemberOnlyDiscount ? 1 : 2;
                    const key = groupPrices.sameMemberOnlyDiscount ? (category.id + "-" + member.id) : category.id;
                    const arr = (_b = groupedItems[prior].get(key)) !== null && _b !== void 0 ? _b : [];
                    arr.push(_item);
                    groupedItems[prior].set(key, arr);
                }
            }
        }
        // Keep a mapping of amount of members that is registered in each category +  each group
        /// Group ID => amount of members that are registered
        const groupAmounts = new Map();
        // Amount of registrations in a given group for a given member (member id is first)
        const groupPerMemberAmounts = new Map();
        /// Category ID => amount of members that are registered
        const categoryAmounts = new Map();
        // Amount of registrations in a given category for a given member (member id is first)
        const categoryPerMemberAmounts = new Map();
        // Fill in the existing registrations
        for (const member of members) {
            // Create map items
            const groupAmountsMember = new Map();
            const categoryAmountsMember = new Map();
            groupPerMemberAmounts.set(member.id, groupAmountsMember);
            categoryPerMemberAmounts.set(member.id, categoryAmountsMember);
            for (const registration of member.registrations) {
                // Skip this if we have an item in the cart with the same properties
                const inCart = !!items.find(i => i.memberId === member.id && i.groupId === registration.groupId);
                if (inCart) {
                    continue;
                }
                const group = groups.find(g => g.id === registration.groupId);
                if (group && !registration.waitingList && registration.cycle === group.cycle && registration.registeredAt !== null && registration.deactivatedAt === null) {
                    // Active registration
                    groupAmounts.set(group.id, ((_c = groupAmounts.get(group.id)) !== null && _c !== void 0 ? _c : 0) + 1);
                    groupAmountsMember.set(group.id, ((_d = groupAmountsMember.get(group.id)) !== null && _d !== void 0 ? _d : 0) + 1);
                    const category = this.getParentCategory(group, categories);
                    if (category) {
                        categoryAmounts.set(category.id, ((_e = categoryAmounts.get(category.id)) !== null && _e !== void 0 ? _e : 0) + 1);
                        categoryAmountsMember.set(category.id, ((_f = categoryAmountsMember.get(category.id)) !== null && _f !== void 0 ? _f : 0) + 1);
                    }
                }
            }
        }
        // All items that are not affected by ordering calculate first
        for (const item of groupedItems[0]) {
            // TODO: cache this information
            const group = item._group;
            const category = item._category;
            let alreadyRegisteredCount = 0;
            if (item._groupPrices.prices.length > 1) {
                if (item._groupPrices.onlySameGroup) {
                    if (item._groupPrices.sameMemberOnlyDiscount) {
                        alreadyRegisteredCount = (_h = (_g = groupPerMemberAmounts.get(item.memberId)) === null || _g === void 0 ? void 0 : _g.get(item.groupId)) !== null && _h !== void 0 ? _h : 0;
                    }
                    else {
                        alreadyRegisteredCount = (_j = groupAmounts.get(item.groupId)) !== null && _j !== void 0 ? _j : 0;
                    }
                }
                else {
                    throw new Error("Category based discount shouldn't be in priority 0 queue");
                    if (item._groupPrices.sameMemberOnlyDiscount) {
                        alreadyRegisteredCount = (_l = (_k = categoryPerMemberAmounts.get(item.memberId)) === null || _k === void 0 ? void 0 : _k.get(category.id)) !== null && _l !== void 0 ? _l : 0;
                    }
                    else {
                        alreadyRegisteredCount = (_m = categoryAmounts.get(category.id)) !== null && _m !== void 0 ? _m : 0;
                    }
                }
            }
            const price = item._groupPrices.getPriceFor(item.reduced, alreadyRegisteredCount);
            item.calculatedPrice = price;
            // Increase counters
            const groupAmountsMember = groupPerMemberAmounts.get(item.memberId);
            const categoryAmountsMember = categoryPerMemberAmounts.get(item.memberId);
            groupAmounts.set(group.id, ((_o = groupAmounts.get(group.id)) !== null && _o !== void 0 ? _o : 0) + 1);
            groupAmountsMember.set(group.id, ((_p = groupAmountsMember.get(group.id)) !== null && _p !== void 0 ? _p : 0) + 1);
            categoryAmounts.set(category.id, ((_q = categoryAmounts.get(category.id)) !== null && _q !== void 0 ? _q : 0) + 1);
            categoryAmountsMember.set(category.id, ((_r = categoryAmountsMember.get(category.id)) !== null && _r !== void 0 ? _r : 0) + 1);
        }
        // Discount for category, for same member
        for (const [_, items] of groupedItems[1]) {
            const memberId = items[0].memberId;
            const group = items[0]._group;
            const category = items[0]._category;
            const maxFamilyMembersDiscount = Math.max(...items.map(i => i._groupPrices.prices.length));
            // Get current amounts
            const existingCount = (_t = (_s = categoryPerMemberAmounts.get(memberId)) === null || _s === void 0 ? void 0 : _s.get(category.id)) !== null && _t !== void 0 ? _t : 0;
            // Find most optimal order
            const combinations = getPossibleCombinations(items, maxFamilyMembersDiscount - 1 - existingCount);
            let minimumPrice = null;
            let minimumCombination = [];
            // Calculate the smallest price
            for (const combination of combinations) {
                let total = 0;
                let alreadyRegisteredCount = existingCount;
                for (const item of combination) {
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    const price = item._groupPrices.getPriceFor(item.reduced, alreadyRegisteredCount);
                    alreadyRegisteredCount++;
                    total += price;
                }
                if (minimumPrice === null || total < minimumPrice) {
                    minimumPrice = total;
                    minimumCombination = combination;
                }
            }
            // Calculate once again to set the fixed price for every item
            let alreadyRegisteredCount = existingCount;
            for (const item of minimumCombination) {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                const price = item._groupPrices.getPriceFor(item.reduced, alreadyRegisteredCount);
                item.calculatedPrice = price;
                alreadyRegisteredCount++;
                // Increase counters for next cycle (groups don't matter anymore, so skipped, and same member also doesn't matter anymore)
                //const groupAmountsMember = groupPerMemberAmounts.get(item.memberId)!
                //const categoryAmountsMember = categoryPerMemberAmounts.get(item.memberId)!
                //groupAmounts.set(group.id, (groupAmounts.get(group.id) ?? 0) + 1)
                //groupAmountsMember.set(group.id, (groupAmountsMember.get(group.id) ?? 0) + 1)
                categoryAmounts.set(category.id, ((_u = categoryAmounts.get(category.id)) !== null && _u !== void 0 ? _u : 0) + 1);
                //categoryAmountsMember.set(category.id, (categoryAmountsMember.get(category.id) ?? 0) + 1)
            }
        }
        // Discount for category, for same member
        for (const [_, items] of groupedItems[2]) {
            const memberId = items[0].memberId;
            const group = items[0]._group;
            const category = items[0]._category;
            const maxFamilyMembersDiscount = Math.max(...items.map(i => i._groupPrices.prices.length));
            // Get current amounts
            const existingCount = (_v = categoryAmounts.get(category.id)) !== null && _v !== void 0 ? _v : 0;
            // Find most optimal order
            const combinations = getPossibleCombinations(items, maxFamilyMembersDiscount - 1 - existingCount);
            let minimumPrice = null;
            let minimumCombination = [];
            // Calculate the smallest price
            for (const combination of combinations) {
                let total = 0;
                let alreadyRegisteredCount = existingCount;
                for (const item of combination) {
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    const price = item._groupPrices.getPriceFor(item.reduced, alreadyRegisteredCount);
                    alreadyRegisteredCount++;
                    total += price;
                }
                if (minimumPrice === null || total < minimumPrice) {
                    minimumPrice = total;
                    minimumCombination = combination;
                }
            }
            // Calculate once again to set the fixed price for every item
            let alreadyRegisteredCount = existingCount;
            for (const item of minimumCombination) {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                const price = item._groupPrices.getPriceFor(item.reduced, alreadyRegisteredCount);
                item.calculatedPrice = price;
                alreadyRegisteredCount++;
                // No need to increase counters: it's done
            }
        }
        // Step 3: calculate all possible prices
        /*for (const [familyID, items] of groupedItems) {
            // Count all members that are already registered in this family discount group
            // (to know the number of members, and to know the discount)
            let existingCount = 0
            for (const member of members) {
                for (const registration of member.registrations) {

                    // Skip this if we have an item in the cart with the same properties
                    const inCart = !!items.find(i => i.memberId === member.id && i.groupId === registration.groupId)
                    if (inCart) {
                        continue
                    }
                    const group = groups.find(g => g.id === registration.groupId)
                    if (group && !registration.waitingList && registration.cycle === group.cycle && registration.registeredAt !== null && registration.deactivatedAt === null) {
                        // Active registration
                        if (this.getFamilyDiscountIdFor(group, categories) == familyID) {
                            existingCount++
                        }
                    }
                }
            }

            // TODO: move those without discounts to the start and remove them from the combinations that need to get calculated. They always need to be added first because they don't give discount
 
            const maxFamilyMembersDiscount = 3 // discount for third + more members

            // Order in all possible ways (where only the first X places matter)
            const combinations = getPossibleCombinations(items, maxFamilyMembersDiscount - 1 - existingCount)

            let minimumPrice: number | null = null
            let minimumCombination: RegisterItemWithPriceAndGroupPrices[] = []

            // Calculate the smallest price
            for (const combination of combinations) {
                let total = 0
                let alreadyRegisteredCount = existingCount
                for (const item of combination) {
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    const price = item._groupPrices.getPriceFor(item.reduced, alreadyRegisteredCount)
                    alreadyRegisteredCount++
                    total += price
                }

                if (minimumPrice === null || total < minimumPrice) {
                    minimumPrice = total
                    minimumCombination = combination
                }
            }

            // Calculate once again to set the fixed price for every item
            let alreadyRegisteredCount = existingCount
            for (const item of minimumCombination) {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                const price = item._groupPrices.getPriceFor(item.reduced, alreadyRegisteredCount)
                item.calculatedPrice = price
                alreadyRegisteredCount++
            }
        }*/
        // Clean up
        for (const item of items) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            const i = item;
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            delete i._groupPrices;
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            delete i._group;
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            delete i._category;
        }
    }
}
/**
 * Return all the possible ordering of the first x items (= amount) of a given array.
 * E.g. [1, 2, 3, 4, 5] for 2 items: [1, 2, (3, 4, 5)], [1, 3, (2, 4, 5)], [1, 4, (2, 3, 5)], [1, 5, (3, 4, 2)], [2, 1, (3, 4, 5)], ...
 */
export function getPossibleCombinations(array, amount) {
    if (amount <= 0) {
        // Only one possibility
        return [array];
    }
    if (array.length <= 1) {
        // Only one possible combination
        return [array];
    }
    // On array.length possibilities
    return array.flatMap((first, index) => {
        // Remove first from the array, get possible combinations, append first back to each combination and return these combinations
        const arr = array.slice();
        arr.splice(index, 1);
        // Get all possibilities
        const combo = getPossibleCombinations(arr, amount - 1);
        // Append first back to all possible combinations
        for (const c of combo) {
            c.unshift(first);
        }
        return combo;
    });
}
//# sourceMappingURL=RegisterCartPriceCalculator.js.map